import { useMemo, useRef, useState } from 'react';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps, SortingType } from 'components/base-table/dto';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { deleteContactType, fetchContactTypeList } from './api';
import ContactTypeTableColumnConfig, { SearchableContactTypeTableColumnConfig } from './contact-types-column-config';
import { Box, Button, Dialog, Stack } from '@mui/material';
import { dispatch, useSelector } from '../../store';
import {
    openContactTypeForm,
    setContactType,
    setContactTypeColumnsPersistence,
    setContactTypeListPersistence,
} from '../../store/reducers/contact-type';
import { PopupTransition } from '../../components/@extended/Transitions';
import ContactTypeForm from './contact-type-form';
import DeleteButton from 'pages/calendar/delete-button';
import { Filters, Row } from 'react-table';
import ContactTypeDto from 'common/dto-types/contacts/contact-type-dto';
import IconButton from 'components/@extended/IconButton';
import { EditOutlined } from '@ant-design/icons';

type ContactTypeListQuery = {
    filters: Filters<any>;
    searchTerm?: string | null;
    sortBy?: string;
    sortingType?: SortingType;
};

const ContactTypeListPage = () => {
    const [contactTypeList, setContactTypeList] = useState<Array<ContactTypeDto>>([]);
    const savedFilters = useSelector((state) => state.contactType.filters);
    const savedSorting = useSelector((state) => state.contactType.sorting);
    const defaultVisibleColumnConfig = useSelector((state) => state.contactType.visibleColumns);

    const isFormOpen = useSelector((state) => state.contactType.isFormOpen);

    const loadContactTypes = async ({ filters, searchTerm, sortBy, sortingType }: ContactTypeListQuery) => {
        const data = await fetchContactTypeList({
            sortBy,
            sortingType,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableContactTypeTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setContactTypeList(data.elements);

            dispatch(
                setContactTypeListPersistence({
                    filters: filters,
                    sorting: { searchTerm, sortBy, sortingType },
                }),
            );
        }
    };

    const fetchData: BaseTableFetchFuncProps = async ({ filters, searchTerm, sortBy, sortingType }) =>
        loadContactTypes({ filters, searchTerm, sortBy, sortingType });

    const handleColumnVisibilityChange = async (newVisibleColumns: string[]) => {
        dispatch(setContactTypeColumnsPersistence(newVisibleColumns));
    };

    const refreshContactTypeList = async () => {
        await loadContactTypes({
            filters: savedFilters,
            searchTerm: savedSorting.searchTerm,
            sortBy: savedSorting.sortBy,
            sortingType: savedSorting.sortingType,
        });
    };

    const refreshContactTypeListRef = useRef(refreshContactTypeList);
    refreshContactTypeListRef.current = refreshContactTypeList;

    const defaultColumnConfig = useMemo(
        () => [
            ...ContactTypeTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => {
                    const contactType = row.row.original as ContactTypeDto;
                    return (
                        <Stack direction="row">
                            <IconButton
                                shape="rounded"
                                onClick={() => {
                                    dispatch(setContactType(contactType));
                                    dispatch(openContactTypeForm());
                                }}
                            >
                                <EditOutlined />
                            </IconButton>
                            <DeleteButton
                                deleteItemName={contactType.name}
                                deleteCallback={() => deleteContactType(String(contactType.id))}
                                refreshCallback={() => refreshContactTypeListRef.current()}
                            />
                        </Stack>
                    );
                },
            },
        ],
        [],
    );

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2 }}>
                    <Button variant="contained" color="primary" onClick={() => dispatch(openContactTypeForm())}>
                        Add Contact Type
                    </Button>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={contactTypeList}
                        fetchData={fetchData}
                        pageCount={0}
                        defaultFilters={savedFilters ?? []}
                        defaultSortingType={savedSorting.sortingType}
                        defaultSearchTerm={savedSorting.searchTerm}
                        defaultColumnIdToSortBy={savedSorting.sortBy}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        onColumnsVisibilityChange={handleColumnVisibilityChange}
                        hidePagination
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <ContactTypeForm refreshCallback={() => refreshContactTypeList()} />
                </Dialog>
            )}
        </MainCard>
    );
};

export default ContactTypeListPage;
